import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import styles from './TaskModal.module.scss';
import {
  IconX,
  IconDeviceFloppy,
  IconTrash,
  IconSearch
} from '@tabler/icons-react';
import { useQueryClient } from '@tanstack/react-query';

import { TaskStatus, TaskDto, PhaseDto, TenantUserDto } from '../../api/model';
import { usePutTaskId, useDeleteTaskId } from '../../api/task-api/task-api';
import { useGetDeliverObject, useGetDeliverObjectProjectIdUnassigned } from '../../api/deliver-object-api/deliver-object-api';
import { useGetProjectIdMembers } from '../../api/project-api/project-api';
import { useNotifications } from '../../hooks/useNotifications';
import {getPhaseDisplayName} from '../../utils/phaseUtils';
import UserPicker from '../ui/UserPicker/UserPicker';
import { memberAsUser } from '../../utils/projectMembers';
import TextLimitHint from '../../components/ui/TextLimitHint/TextLimitHint';
import { SHORT_DESCRIPTION } from '../../utils/textLimits';

interface EditTaskModalProps {
  isOpen: boolean;
  onClose: () => void;
  task: TaskDto | null;
  onTaskUpdated?: () => void;
  onTaskDeleted?: () => void;
  phases?: PhaseDto[];
  projectId?: number;
}

interface TaskFormData {
  name: string;
  description: string;
  assignedUserIds: number[];
  status: TaskStatus;
  deliverObjectId?: number;
}

// Confirmation Component
interface ConfirmationDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  isDestructive?: boolean;
}

const ConfirmationDialog: React.FC<ConfirmationDialogProps> = ({
  isOpen,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isDestructive = false
}) => {
  if (!isOpen) return null;

  return (
    <div className={styles.confirmOverlay} onClick={onCancel}>
      <div className={styles.confirmDialog} onClick={(e) => e.stopPropagation()}>
        <h3 className={styles.confirmTitle}>{title}</h3>
        <p className={styles.confirmMessage}>{message}</p>
        <div className={styles.confirmActions}>
          <button
            type="button"
            className={styles.cancelButton}
            onClick={onCancel}
          >
            {cancelText}
          </button>
          <button
            type="button"
            className={isDestructive ? styles.deleteButton : styles.submitButton}
            onClick={onConfirm}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};

const EditTaskModal: React.FC<EditTaskModalProps> = ({
  isOpen,
  onClose,
  task,
  onTaskUpdated,
  onTaskDeleted,
  phases = [],
  projectId,
}) => {
  const { t } = useTranslation('tasks');
  const [formData, setFormData] = useState<TaskFormData>({
    name: '',
    description: '',
    assignedUserIds: [],
    status: TaskStatus.PLANNED,
    deliverObjectId: undefined
  });

  const [deliverableSearch, setDeliverableSearch] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const formRef = React.useRef<HTMLFormElement>(null);

  // hooks
  const queryClient = useQueryClient();
  const updateTaskMutation = usePutTaskId();
  const deleteTaskMutation = useDeleteTaskId();
  const { data: allDeliverObjects, isLoading: isLoadingDeliverObjects } = useGetDeliverObject();
  const { data: unassignedDeliverable } = useGetDeliverObjectProjectIdUnassigned(
    projectId ?? 0,
    { query: { enabled: !!projectId } }
  );
  const { notifySuccess, notifyError } = useNotifications();
  const { data: projectMembers } = useGetProjectIdMembers(projectId ?? 0, {
    query: { enabled: !!projectId },
  });
  const memberUsers = useMemo<TenantUserDto[]>(
    () => (projectMembers ?? []).map(memberAsUser),
    [projectMembers]
  );

  // Filter deliverables
  const filteredDeliverables = useMemo(() => {
    if (!allDeliverObjects) return [];
    
    if (!deliverableSearch.trim()) return allDeliverObjects;
    
    const searchLower = deliverableSearch.toLowerCase();
    return allDeliverObjects.filter((obj: any) => 
      obj.name?.toLowerCase().includes(searchLower) ||
      obj.criticality?.toLowerCase().includes(searchLower)
    );
  }, [allDeliverObjects, deliverableSearch]);

  // current deliverable name
  const currentDeliverable = useMemo(() => {
    if (!allDeliverObjects || !task?.deliverObjectId) return null;
    return allDeliverObjects.find((obj: any) => obj.id === task.deliverObjectId);
  }, [allDeliverObjects, task?.deliverObjectId]);

  // task currently sits in the hidden greenfield "unassigned" container
  const isTaskUnassigned = !!(
    unassignedDeliverable?.id &&
    task?.deliverObjectId &&
    unassignedDeliverable.id === task.deliverObjectId
  );

  // phase 
  const currentPhaseName = useMemo(() => {
    if (!currentDeliverable?.phaseId || !phases.length) return null;
    const phase = phases.find(p => p.id === currentDeliverable.phaseId);
    if (!phase) return null;
      return getPhaseDisplayName(phase);
  }, [currentDeliverable, phases]);

  // Initialize form with task data
  useEffect(() => {
    if (task && isOpen) {
      setFormData({
        name: task.name || '',
        description: task.description || '',
        assignedUserIds: task.assignedUserIds ?? [],
        status: task.status || TaskStatus.PLANNED,
        deliverObjectId: task.deliverObjectId
      });


    }
  }, [task, isOpen]);

  const handleInputChange = (field: keyof TaskFormData, value: any) => {
    setFormData(prev => {
      const updated = {
        ...prev,
        [field]: value
      };

      if (field === 'status') {
        const statusProgressMap: Record<TaskStatus, number> = {
          [TaskStatus.BACKLOG]: 0,
          [TaskStatus.PLANNED]: 0,
          [TaskStatus.IN_PROGRESS]: 50,
          [TaskStatus.IN_REVIEW]: 75,
          [TaskStatus.FINISHED]: 100,
          [TaskStatus.CANCELLED]: 0
        };
      }
      
      return updated;
    });
  };

  const validateForm = (): string | null => {
    if (!formData.name.trim()) {
      return t('edit.validation.nameRequired');
    }
    if (formData.name.length > 50) {
      return t('edit.validation.nameMaxLength');
    }
    if (formData.description.length > 250) {
      return t('edit.validation.descMaxLength');
    }
    if (!formData.deliverObjectId) {
      return t('edit.validation.deliverableRequired');
    }
    return null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!task?.id) {
      notifyError(t('edit.validation.updateFailed'), t('edit.validation.idMissing'));
      return;
    }

    const validationError = validateForm();
    if (validationError) {
      notifyError(t('edit.title'), validationError);
      return;
    }

    setIsSubmitting(true);

    try {
      // Map status to progress
      const statusProgressMap: Record<TaskStatus, number> = {
        [TaskStatus.BACKLOG]: 0,
        [TaskStatus.PLANNED]: 0,
        [TaskStatus.IN_PROGRESS]: 50,
        [TaskStatus.IN_REVIEW]: 75,
        [TaskStatus.FINISHED]: 100,
        [TaskStatus.CANCELLED]: 0
      };

      const progress = statusProgressMap[formData.status];

      // Create task data 
      const taskData: TaskDto = {
        id: task.id,
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        assignedUserIds: formData.assignedUserIds,
        status: formData.status,
        progress: progress,
        deliverObjectId: formData.deliverObjectId
      };

      await updateTaskMutation.mutateAsync({
        id: task.id,
        data: taskData
      });
      await Promise.all([
          queryClient.invalidateQueries({predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/task')}),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/intelligence') }),
        queryClient.invalidateQueries({ queryKey: ['/project'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).includes('/deliverobject') })
      ]);

      notifySuccess(t('edit.title'), t('edit.validation.updateSuccess', { name: formData.name }));
      onTaskUpdated?.();
      onClose();
    } catch (error) {
      console.error('Error updating task:', error);
      notifyError(t('edit.title'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!task?.id) {
      notifyError(t('edit.validation.deleteFailed'), t('edit.validation.idMissing'));
      return;
    }

    setIsSubmitting(true);

    try {
      await deleteTaskMutation.mutateAsync({
        id: task.id
      });

      await Promise.all([
          queryClient.invalidateQueries({predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/task')}),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).startsWith('/intelligence') }),
        queryClient.invalidateQueries({ queryKey: ['/project'] }),
        queryClient.invalidateQueries({ queryKey: ['projects'] }),
        queryClient.invalidateQueries({ predicate: (query) => typeof query.queryKey[0] === 'string' && (query.queryKey[0] as string).includes('/deliverobject') })
      ]);

      notifySuccess(t('edit.title'), t('edit.validation.deleteSuccess', { name: task.name }));
      onTaskDeleted?.();
      setShowDeleteConfirm(false);
      onClose();
    } catch (error) {
      console.error('Error deleting task:', error);
      notifyError(t('edit.title'), error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
    }
  };

  if (!isOpen || !task) return null;

  return (
    <>
      <div className={styles.modalOverlay} onClick={handleClose}>
        <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modalHeader}>
            <div className={styles.headerContent}>
              <h2 className={styles.modalTitle}>
                {t('edit.title')}
              </h2>
              <p className={styles.headerDescription}>{t('edit.description')}</p>
            </div>
            <button className={styles.closeButton} onClick={handleClose} type="button" disabled={isSubmitting}>
              <IconX size={20} />
            </button>
          </div>

          <div className={styles.modalBody}>
            <form ref={formRef} onSubmit={handleSubmit}>
              <div className={styles.formSection}>
                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.taskName')}
                    <span className={styles.required}>*</span>
                  </label>
                  <input
                    type="text"
                    className={styles.formInput}
                    placeholder={t('create.fields.namePlaceholder')}
                    value={formData.name}
                    onChange={(e) => handleInputChange('name', e.target.value)}
                    disabled={isSubmitting}
                    required
                    maxLength={50}
                  />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.description')}
                  </label>
                  <textarea
                    className={styles.formTextarea}
                    placeholder={t('create.fields.descriptionPlaceholder')}
                    value={formData.description}
                    onChange={(e) => handleInputChange('description', e.target.value)}
                    disabled={isSubmitting}
                    rows={4}
                    maxLength={SHORT_DESCRIPTION}
                  />
                  <TextLimitHint value={formData.description} max={SHORT_DESCRIPTION} />
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.assignedPerson')}
                  </label>
                  <UserPicker
                    multi
                    value={formData.assignedUserIds}
                    onChange={(ids) => handleInputChange('assignedUserIds', ids)}
                    disabled={isSubmitting}
                    placeholder={t('create.fields.assignedPersonPlaceholder')}
                    users={projectId ? memberUsers : undefined}
                  />
                </div>
              </div>

              <div className={styles.formSection}>
                <h3 className={styles.sectionTitle}>
                  {t('edit.fields.status')}
                </h3>

                <div className={styles.formGroup}>
                  <select
                    className={styles.formSelect}
                    value={formData.status}
                    onChange={(e) => handleInputChange('status', e.target.value as TaskStatus)}
                    disabled={isSubmitting}
                  >
                    <option value={TaskStatus.PLANNED}>{t('edit.fields.statusTodo')}</option>
                    <option value={TaskStatus.IN_PROGRESS}>{t('edit.fields.statusInProgress')}</option>
                    <option value={TaskStatus.IN_REVIEW}>{t('edit.fields.statusInReview')}</option>
                    <option value={TaskStatus.FINISHED}>{t('edit.fields.statusDone')}</option>
                  </select>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.currentDeliverable')}
                  </label>
                  <div className={styles.formInput} style={{ 
                    backgroundColor: '#F9FAFB', 
                    color: '#374151',
                    cursor: 'default',
                    border: '1px solid #E5E7EB'
                  }}>
                    {currentDeliverable ? (
                        (currentDeliverable.name || 'Unnamed deliverable').replaceAll('_', ' ')
                    ) : isTaskUnassigned ? (
                      <span style={{ color: '#9CA3AF' }}>{t('unassigned.title')}</span>
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>{t('edit.fields.noDeliverable')}</span>
                    )}
                  </div>
                </div>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.phase')}
                  </label>
                  <div className={styles.formInput} style={{
                    backgroundColor: '#F9FAFB',
                    color: '#374151',
                    cursor: 'default',
                    border: '1px solid #E5E7EB'
                  }}>
                    {currentPhaseName ? (
                      currentPhaseName
                    ) : (
                      <span style={{ color: '#9CA3AF' }}>{t('edit.fields.noPhase')}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.formSection}>

                <div className={styles.formGroup}>
                  <label className={styles.formLabel}>
                    {t('edit.fields.deliverable')} <span className={styles.required}>*</span>
                  </label>
                  
                  {isLoadingDeliverObjects ? (
                    <div className={styles.formInput} style={{ display: 'flex', alignItems: 'center', color: '#6B7280' }}>
                      {t('edit.fields.loadingDeliverables')}
                    </div>
                  ) : allDeliverObjects && allDeliverObjects.length > 0 ? (
                    <>
                      <div style={{ position: 'relative', marginBottom: '8px' }}>
                        <IconSearch 
                          size={18} 
                          style={{ 
                            position: 'absolute', 
                            left: '12px', 
                            top: '50%', 
                            transform: 'translateY(-50%)', 
                            color: '#6B7280',
                            pointerEvents: 'none'
                          }} 
                        />
                        <input
                          type="text"
                          className={styles.formInput}
                          value={deliverableSearch}
                          onChange={(e) => setDeliverableSearch(e.target.value)}
                          placeholder={t('edit.fields.searchDeliverables')}
                          style={{ paddingLeft: '40px' }}
                          disabled={isSubmitting}
                        />
                      </div>
                      
                      <div className={styles.deliverableList}>
                        {filteredDeliverables.map((obj) => (
                          <div
                            key={obj.id}
                            className={`${styles.deliverableItem} ${formData.deliverObjectId === obj.id ? styles.selected : ''}`}
                            onClick={() => !isSubmitting && handleInputChange('deliverObjectId', obj.id)}
                            style={{ cursor: isSubmitting ? 'not-allowed' : 'pointer' }}
                          >
                              <div
                                  className={styles.deliverableName}>{(obj.name || t('create.deliverables.unnamedItem')).replaceAll('_', ' ')}</div>
                            {(obj.criticality || obj.endDate) && (
                              <div className={styles.deliverableMeta}>
                                {obj.criticality && (
                                  <span className={styles.deliverableMetaItem}>
                                    {obj.criticality}
                                  </span>
                                )}
                                {obj.endDate && (
                                  <span className={styles.deliverableMetaItem}>
                                    Due: {obj.endDate}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                      
                      {filteredDeliverables.length === 0 && deliverableSearch && (
                        <p className={styles.inputHelper}>
                          {t('create.deliverables.noMatch')}
                        </p>
                      )}
                    </>
                  ) : (
                    <div className={styles.inputHelper} style={{ padding: '12px', backgroundColor: '#FEF3C7', borderRadius: '6px', color: '#92400E' }}>
                      {t('edit.fields.noDeliverables')}
                    </div>
                  )}
                </div>
              </div>
            </form>
          </div>

          <div className={styles.modalFooter}>
            <button
              type="button"
              className={styles.deleteButton}
              onClick={() => setShowDeleteConfirm(true)}
              disabled={isSubmitting}
            >
              <IconTrash size={18} />
              {t('edit.buttons.delete')}
            </button>
            <div className={styles.footerActions}>
              <button
                type="button"
                className={styles.cancelButton}
                onClick={handleClose}
                disabled={isSubmitting}
              >
                {t('create.buttons.cancel')}
              </button>
              <button
                type="button"
                className={styles.submitButton}
                disabled={isSubmitting}
                onClick={() => formRef.current?.requestSubmit()}
              >
                <IconDeviceFloppy size={18} />
                {isSubmitting ? t('edit.buttons.saving') : t('edit.buttons.save')}
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmationDialog
        isOpen={showDeleteConfirm}
        title={t('edit.buttons.delete')}
        message={t('edit.messages.deleteConfirm', { name: task?.name || 'this task' })}
        confirmText={t('edit.buttons.delete')}
        cancelText={t('create.buttons.cancel')}
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        isDestructive={true}
      />
    </>
  );
};

export default EditTaskModal;
